import { Injectable } from '@angular/core';
import { OAuthStorage } from 'angular-oauth2-oidc';

/**
 * The keys that have to survive the redirect to Cardinal and back.
 *
 * `nonce` travels inside the `state` parameter and is checked when the code comes back;
 * `PKCE_verifier` is what proves the token request belongs to the same client that started the
 * authorization request. Both are worthless on their own, both are valid for seconds, and without
 * them a login cannot complete -- so they go to `sessionStorage`, which is per tab and dies with it.
 *
 * `requested_route` is the library's own deep-link memory. It is a path, not a credential, and it is
 * listed here so `preserveRequestedRoute` keeps working if it is ever switched on.
 *
 * Everything not named here is token material and stays in memory.
 */
const REDIRECT_SURVIVING_KEYS: readonly string[] = ['nonce', 'PKCE_verifier', 'requested_route'];

/** What one tab hands another: the token half of the storage, and nothing else. */
export type SessionSnapshot = Readonly<Record<string, string>>;

/**
 * The storage `angular-oauth2-oidc` writes its tokens into -- deliberately not a browser storage.
 *
 * The library keeps everything in one place, and its default is `sessionStorage`. Under the
 * authorization code flow that place gains a `refresh_token`, and a refresh token in a browser
 * storage is a credential any script on the origin can read, for as long as the tab lives. Cardinal
 * runs in degraded mode: it can neither notice a reused refresh token nor revoke one. So that trade
 * is not available here, and the split below is what replaces it.
 *
 * The price is that a reload finds no session. That is answered by the `prompt=none` redirect and
 * the handover between tabs in `AuthenticationService` -- neither of which costs the user a login.
 *
 * **Do not add a token key to the allowlist to fix a reload.** The reload is supposed to find
 * nothing; if it does not work, the answer is in the sign-in orchestration, not here.
 */
@Injectable({
  providedIn: 'root'
})
export class MemoryOAuthStorage implements OAuthStorage {
  private readonly entries = new Map<string, string>();

  public getItem(key: string): string | null {
    if (survivesRedirect(key)) {
      return sessionStorage.getItem(key);
    }

    return this.entries.get(key) ?? null;
  }

  public setItem(key: string, data: string): void {
    if (survivesRedirect(key)) {
      sessionStorage.setItem(key, data);

      return;
    }

    this.entries.set(key, data);
  }

  public removeItem(key: string): void {
    if (survivesRedirect(key)) {
      sessionStorage.removeItem(key);

      return;
    }

    this.entries.delete(key);
  }

  /**
   * The session as a plain object, for handing to another tab.
   *
   * Only the in-memory half travels. The verifier and the nonce are tab-local by design: two tabs
   * starting a login at the same time must not share them, or the second would overwrite what the
   * first is waiting for.
   */
  public snapshot(): SessionSnapshot {
    return Object.fromEntries(this.entries);
  }

  /**
   * Takes over a session from another tab. Replaces rather than merges -- a half-overwritten token
   * set is worse than none, because the expiry and the token would no longer belong together.
   */
  public restore(snapshot: SessionSnapshot): void {
    this.entries.clear();

    for (const [key, value] of Object.entries(snapshot)) {
      this.entries.set(key, value);
    }
  }

  /** Drops the session. The redirect keys are left alone -- a login in flight still needs them. */
  public clearSession(): void {
    this.entries.clear();
  }

  /**
   * Removes what an abandoned login left behind.
   *
   * A `prompt=none` attempt that finds no Cardinal session is never redeemed, so nothing consumes
   * its verifier. Harmless in itself, but it accumulates, and a stale verifier is exactly what makes
   * the *next* login fail with a mismatched code.
   */
  public clearPendingLogin(): void {
    for (const key of REDIRECT_SURVIVING_KEYS) {
      sessionStorage.removeItem(key);
    }
  }
}

function survivesRedirect(key: string): boolean {
  return REDIRECT_SURVIVING_KEYS.includes(key);
}
